import { useFormContext, type FieldValues } from "react-hook-form";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import { useEffect } from "react";
import {
  durationOptions,
  durationDictionary,
  durationOptionSchema,
  type IDurationOption,
} from "./activityDurationModels";
import {
  CustomActivityDuration,
  type ICustomActivityDurationFields,
} from "./CustomActivityDuration";
import { useLingui } from "@lingui/react/macro";
import { errorMessageAt, withPrefix } from "@/forms/fieldPath";

export type IActivityDurationFields = ICustomActivityDurationFields & {
  durationOption: IDurationOption;
};

export const ActivityDuration = ({ namePrefix }: { namePrefix?: string }) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    watch,
    unregister,
  } = useFormContext<FieldValues>();

  const durationOptionPath = withPrefix(namePrefix, "durationOption");
  const customDurationPath = withPrefix(namePrefix, "customDuration");
  const customDurationUnitPath = withPrefix(namePrefix, "customDurationUnit");
  const durationOptionError = errorMessageAt(errors, durationOptionPath);

  const selectedDuration = watch(durationOptionPath);
  const isCustomDuration =
    selectedDuration === durationOptionSchema.enum.custom;

  // Unregister the custom duration fields if not using custom
  useEffect(() => {
    if (!isCustomDuration) {
      unregister(customDurationPath);
      unregister(customDurationUnitPath);
    }
  }, [
    isCustomDuration,
    unregister,
    customDurationPath,
    customDurationUnitPath,
  ]);

  return (
    <>
      <RadioButtons
        legend={t`Tidsåtgång`}
        errorMessage={durationOptionError}
        orientation="horizontal"
      >
        {durationOptions.map((option) => (
          <RadioButton
            key={option}
            label={{ text: t(durationDictionary[option].displayText) }}
            visualStyle="framed"
            value={option}
            {...register(durationOptionPath, {
              required: {
                value: true,
                message: t`Välj tidsåtgång`,
              },
            })}
          />
        ))}
      </RadioButtons>
      {isCustomDuration ? (
        <CustomActivityDuration namePrefix={namePrefix} />
      ) : null}
    </>
  );
};
